/**
 * TerritoryEditModal - Edit default quote settings for a single county
 * Ported from legacy territoriesAndDefaultQuotes.jsx edit modal
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { OathModal, OathButton, OathInput, OathAlert } from '../../../shared/OathUI';
import { updateCountyDefaults } from '../../../../services/oathTransferBondService';
import { formatCurrency } from '../shared/formatUtils';

const TerritoryEditModal = ({ isOpen, onClose, countyData, onSave }) => {
  const [miscFee, setMiscFee] = useState('');
  const [travelFee, setTravelFee] = useState('');
  const [quoteDuration, setQuoteDuration] = useState('');
  const [timeToPost, setTimeToPost] = useState('');
  const [isPreferred, setIsPreferred] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Populate form when countyData changes
  useEffect(() => {
    if (countyData) {
      setMiscFee(countyData.tbondMisQuote?.toString() || '');
      setTravelFee(countyData.tbondTravelQuote?.toString() || '');
      setQuoteDuration(countyData.quoteDuration ? (countyData.quoteDuration / 60).toString() : '');
      setTimeToPost(countyData.timeToPost ? (countyData.timeToPost / 60).toString() : '');
      setIsPreferred(countyData.isPreferred || false);
      setError('');
    }
  }, [countyData]);

  const handleSave = async () => {
    setSaving(true);
    setError('');

    const payload = {
      tbondExecutionFeeQuote: countyData.tbondExecutionFeeQuote || null,
      tbondMisQuote: miscFee ? parseFloat(miscFee) : null,
      tbondTravelQuote: travelFee ? parseFloat(travelFee) : null,
      quoteDuration: quoteDuration ? parseFloat(quoteDuration) * 60 : null,
      timeToPost: timeToPost ? parseFloat(timeToPost) * 60 : null,
      isPreferred,
    };

    const response = await updateCountyDefaults(countyData.agentCountyID, payload);
    if (response.success) {
      toast.success('Territory updated successfully.');
      onSave();
      onClose();
    } else {
      toast.error(response.error || 'Failed to save changes.');
      setError(response.error || 'Failed to save changes.');
    }

    setSaving(false);
  };

  if (!countyData) return null;

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit Defaults — ${countyData.county}, ${countyData.state}`}
      size="md"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSave}
            loading={saving}
            className="w-full sm:w-auto"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-5">
        {error && <OathAlert variant="error" message={error} />}

        {/* Read-only execution fee */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Execution Fee: <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(countyData.tbondExecutionFeeQuote)}</span>
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Execution fee is standard and cannot be changed.</p>
        </div>

        {/* Editable fee fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <OathInput
            label="Misc Fee"
            type="number"
            value={miscFee}
            onChange={setMiscFee}
            prefix="$"
            placeholder="0"
          />
          <OathInput
            label="Travel Fee"
            type="number"
            value={travelFee}
            onChange={setTravelFee}
            prefix="$"
            placeholder="0"
          />
        </div>

        {/* Time fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <OathInput
            label="Quote Duration (hours)"
            type="number"
            value={quoteDuration}
            onChange={setQuoteDuration}
            placeholder="e.g., 3"
            helperText="How long your quote stays valid"
          />
          <OathInput
            label="Time to Post (hours)"
            type="number"
            value={timeToPost}
            onChange={setTimeToPost}
            placeholder="e.g., 4"
            helperText="How long it takes you to post"
          />
        </div>

        {/* Preferred checkbox */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isPreferred}
            onChange={(e) => setIsPreferred(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Preferred Territory</span>
        </label>
      </div>
    </OathModal>
  );
};

export default TerritoryEditModal;
